import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'bootstrap/dist/css/bootstrap.min.css';
import './CustomerMap.css';

// Leaflet's default marker icon paths break under bundlers like webpack;
// point them at the bundled assets explicitly.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [7.9465, -1.0232]; // Ghana, used when no customers have a saved location
const DEFAULT_ZOOM = 7;

const CustomerMap = ({ userEmail, userRole }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        if (!userEmail || !userRole) return;

        setLoading(true);
        setError(null);
        const endpoint = userRole === 'User'
            ? `${baseUrl}api/ProspCustomers/GetAllCustomersOfUser?userEmail=${userEmail}`
            : `${baseUrl}api/ProspCustomers/GetAllCustomers`;

        axios.get(endpoint)
            .then(response => {
                setCustomers(response.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Unable to load customers.');
                setLoading(false);
            });
    }, [userEmail, userRole, baseUrl]);

    const locatedCustomers = useMemo(
        () => customers.filter(c => c.flt1 != null && c.flt2 != null && !(c.flt1 === 0 && c.flt2 === 0)),
        [customers]
    );

    const mapCenter = locatedCustomers.length > 0
        ? [locatedCustomers[0].flt1, locatedCustomers[0].flt2]
        : DEFAULT_CENTER;

    const handleViewDetails = (customerId) => {
        navigate(`/customer-view/${customerId}`);
    };

    return (
        <div className="main-layout">
            <div className="main-content">
                <div className="container mt-5">
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap header">
                        <h1>Customer Map</h1>
                    </div>

                    {loading ? (
                        <div className="d-flex justify-content-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : (
                        <>
                            {locatedCustomers.length === 0 ? (
                                <div className="alert alert-info">
                                    No customers have a saved location yet.
                                </div>
                            ) : (
                                <p className="text-muted">
                                    Showing {locatedCustomers.length} of {customers.length} customers with a saved location.
                                </p>
                            )}
                            <div className="customer-map-wrapper">
                                <MapContainer
                                    center={mapCenter}
                                    zoom={locatedCustomers.length > 0 ? 12 : DEFAULT_ZOOM}
                                    scrollWheelZoom={true}
                                    className="customer-map"
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {locatedCustomers.map(customer => (
                                        <Marker
                                            key={customer.msextmjIndex}
                                            position={[customer.flt1, customer.flt2]}
                                        >
                                            <Popup>
                                                <strong>{customer.vcmx2}</strong><br />
                                                {customer.vcmx1}<br />
                                                {customer.vcmx4}<br />
                                                <button
                                                    className="btn btn-sm btn-view-details mt-2"
                                                    onClick={() => handleViewDetails(customer.msextmjIndex)}
                                                >
                                                    View Details
                                                </button>
                                            </Popup>
                                        </Marker>
                                    ))}
                                </MapContainer>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerMap;
